import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-[#F5F5F5] flex flex-col">
      <header className="bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center text-[#424E59] hover:text-[#1D2D44] text-sm">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Link>
          <span className="font-serif text-lg text-[#1D2D44]">Terms of Service</span>
          <div className="w-16"></div>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto px-4 py-10">
        <h1 className="font-serif text-3xl text-[#1D2D44] mb-1" data-testid="terms-title">Our Love <span style={{color:"#C9A84C"}}>&#9829;</span> Connection</h1>
        <h2 className="font-serif text-xl text-[#1D2D44] mb-1">Terms of Service &amp; Refund Policy</h2>
        <p className="text-[#9AA5A6] text-sm mb-10">Last updated: July 2026</p>

        <div className="space-y-8 text-[#3A4A5A] text-sm leading-relaxed" data-testid="terms-content">

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">Acceptance of Terms</h3>
            <p>By creating an account, purchasing a membership, or using Our Love <span style={{color:"#C9A84C"}}>&#9829;</span> Connection (&ldquo;the Platform&rdquo;), you agree to be bound by these Terms of Service, our Privacy Policy, and all related policies. If you do not agree, please do not use the Platform.</p>
          </section>

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">Memberships &amp; Payments</h3>
            <div className="space-y-3">
              <p><span className="text-[#1D2D44] font-medium">2.1. Membership Types:</span> Memberships are available in varying durations (1 month, 3 months, 6 months, 12 months) and pricing tiers. All prices are clearly displayed prior to purchase.</p>
              <p><span className="text-[#1D2D44] font-medium">2.2. Billing:</span> Membership fees are charged in advance for the selected term and are non-transferable.</p>
              <p><span className="text-[#1D2D44] font-medium">2.3. Automatic Renewal:</span> If you choose a plan with auto-renewal, your membership will renew automatically at the end of each term unless you cancel prior to renewal.</p>
            </div>
          </section>

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">Refunds</h3>
            <div className="space-y-3">
              <p><span className="text-[#1D2D44] font-medium">3.1. 72-Hour Refund Window:</span> You may request a full refund within 72 hours of your initial purchase. Requests must be submitted via <a href="mailto:hello@ourloveconnection.co" className="text-[#B89B5E] underline">hello@ourloveconnection.co</a></p>
              <p><span className="text-[#1D2D44] font-medium">3.2. No Refunds After 72 Hours:</span> After the 72-hour window, all membership fees are non-refundable regardless of account usage, early cancellation, or account deletion.</p>
              <p><span className="text-[#1D2D44] font-medium">3.3. No Prorated or Partial Refunds:</span> There are no prorated refunds for unused time if you cancel or delete your account after the initial 72-hour period.</p>
              <p><span className="text-[#1D2D44] font-medium">3.4. Roll-Over Policy:</span> Membership periods are consecutive and begin on the date of purchase. Unused time does not roll over.</p>
            </div>
          </section>

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">Cancellation &amp; Account Deletion</h3>
            <div className="space-y-3">
              <p><span className="text-[#1D2D44] font-medium">4.1. Cancellation:</span> You may cancel auto-renewal at any time through your account settings. Your membership remains active until the end of your paid term.</p>
              <p><span className="text-[#1D2D44] font-medium">4.2. Account Deletion:</span> You may delete your account at any time. Deletion is immediate and permanent and does not entitle you to a refund.</p>
            </div>
          </section>

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">User Conduct &amp; Community Promise</h3>
            <p className="mb-3">By joining Our Love <span style={{color:"#C9A84C"}}>&#9829;</span> Connection, you agree to our Community Promise:</p>
            <ul className="space-y-2 pl-5">
              <li className="flex items-start"><span className="w-1.5 h-1.5 bg-[#B89B5E] rounded-full mt-1.5 mr-3 flex-shrink-0"></span><span><span className="text-[#1D2D44] font-medium">Show Commitment:</span> Participate with honesty and intent</span></li>
              <li className="flex items-start"><span className="w-1.5 h-1.5 bg-[#B89B5E] rounded-full mt-1.5 mr-3 flex-shrink-0"></span><span><span className="text-[#1D2D44] font-medium">Communicate Respectfully:</span> Respond thoughtfully, no harassment or abuse</span></li>
              <li className="flex items-start"><span className="w-1.5 h-1.5 bg-[#B89B5E] rounded-full mt-1.5 mr-3 flex-shrink-0"></span><span><span className="text-[#1D2D44] font-medium">Practice Comprehension:</span> Listen and seek to understand</span></li>
            </ul>
            <p className="mt-3">Users are responsible for their own decisions, actions, and relationships. Our Love <span style={{color:"#C9A84C"}}>&#9829;</span> Connection provides reflective prompts and informational content but does not direct, control, or determine user behavior, outcomes, or relationship choices.</p>
          </section>

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">Not Therapy or Professional Advice</h3>
            <p>Our Love <span style={{color:"#C9A84C"}}>&#9829;</span> Connection is a self-guided reflection platform. It is not therapy, counseling, coaching, medical care, or mental health treatment. The platform does not provide diagnoses, treatment, or professional advice. If you are experiencing emotional distress, please seek help from a qualified mental health professional.</p>
          </section>

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">Eligibility</h3>
            <p>You must be at least 19 years old to use the Platform. By creating an account, you confirm you meet this requirement.</p>
          </section>

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">Profile, Photos &amp; Verification</h3>
            <p>You agree to provide accurate information, recent photos, and comply with moderation and verification requests. Fraudulent or misleading information may result in account suspension or removal.</p>
          </section>

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">Privacy &amp; Data Protection</h3>
            <p>Your data is handled in accordance with our <Link to="/privacy" className="text-[#B89B5E] underline">Privacy Policy</Link>. We do not sell personal data to advertisers or third parties. You may request account deletion and data removal at any time.</p>
          </section>

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">Safety &amp; Limitations</h3>
            <p>Our Love <span style={{color:"#C9A84C"}}>&#9829;</span> Connection does not conduct criminal background checks. We encourage safe practices, reporting of concerns, and responsible decision-making in all interactions.</p>
          </section>

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">Modifications to Service or Terms</h3>
            <p>We reserve the right to modify these Terms, membership offerings, or features at any time. Continued use of the Platform constitutes acceptance of any changes.</p>
          </section>

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">Limitation of Liability</h3>
            <p>The Platform is provided &ldquo;as is&rdquo; and &ldquo;as available.&rdquo; Our Love <span style={{color:"#C9A84C"}}>&#9829;</span> Connection is not responsible for actions of members or third parties. We do not guarantee the success or outcome of any connection or relationship.</p>
          </section>

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">Governing Law</h3>
            <p>These Terms shall be governed by the laws of the State of Missouri, United States.</p>
          </section>

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">Dispute Resolution</h3>
            <p>Any disputes arising from use of the Platform will first be addressed through good faith communication at <a href="mailto:hello@ourloveconnection.co" className="text-[#B89B5E] underline">hello@ourloveconnection.co</a> before any formal legal action is pursued.</p>
          </section>

          <section>
            <h3 className="font-serif text-lg text-[#1D2D44] mb-2">Contact</h3>
            <p>For support, refunds, questions, or legal inquiries contact us at: <a href="mailto:hello@ourloveconnection.co" className="text-[#B89B5E] underline">hello@ourloveconnection.co</a></p>
          </section>

          <div className="pt-6 border-t border-[#B89B5E]/20">
            <p className="text-[#424E59] italic">By using the Platform, you acknowledge that you have read, understood, and agreed to these Terms of Service and Refund Policy.</p>
          </div>

        </div>

        <div className="mt-12 pt-8 border-t border-[#B89B5E]/20">
          <p className="text-sm text-[#9AA5A6]">
            See also: <Link to="/privacy" className="underline hover:text-[#1D2D44]">Privacy Policy</Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default TermsPage;
